// useBoardInteraction.js
// Tahta tıklama mantığı (oyun ve bulmaca sayfalarında ortak): taş seçme, yasal hedefleri
// gösterme, terfi sorusu, elden taş sürme. Hamle tamamlandığında onMove(descriptor) çağrılır;
// descriptor motorun applyDescriptor() biçimindedir:
//   { kind: 'move', from, to, promote } | { kind: 'drop', pieceType, to }

import { useCallback, useState } from 'react';
import alertify from '../lib/alertifySetup';
import { useLang } from '../i18n';

export default function useBoardInteraction(engine, { color, canAct, onMove }) {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [dropType, setDropType] = useState(null);
  const [targets, setTargets] = useState([]); // legalMoves() veya legalDrops() çıktısı

  const clear = useCallback(() => {
    setSelected(null);
    setDropType(null);
    setTargets([]);
  }, []);

  const askPromote = useCallback(
    () =>
      new Promise((resolve) => {
        alertify
          .confirm(t('promote.title'), t('promote.text'), () => resolve(true), () => resolve(false))
          .set('labels', { ok: t('common.yes'), cancel: t('common.no') });
      }),
    [t]
  );

  const selectPiece = useCallback(
    (row, col) => {
      setDropType(null);
      setSelected({ row, col });
      setTargets(engine.legalMoves({ row, col }));
    },
    [engine]
  );

  const handleSquareClick = useCallback(
    async (row, col) => {
      if (!canAct) return;
      const board = engine.board();
      const piece = board[row][col];

      if (dropType) {
        const ok = targets.some((p) => p.row === row && p.col === col);
        clear();
        if (ok) onMove({ kind: 'drop', pieceType: dropType, to: { row, col } });
        else if (piece && piece.color === color) selectPiece(row, col);
        return;
      }

      if (selected) {
        const m = targets.find((x) => x.to.row === row && x.to.col === col);
        if (m) {
          let promote = false;
          if (m.mustPromote) promote = true;
          else if (m.canPromote) promote = await askPromote();
          const from = selected;
          clear();
          onMove({ kind: 'move', from, to: { row, col }, promote });
          return;
        }
        if (piece && piece.color === color && !(selected.row === row && selected.col === col)) {
          selectPiece(row, col);
        } else {
          clear();
        }
        return;
      }

      if (piece && piece.color === color) selectPiece(row, col);
    },
    [canAct, engine, dropType, targets, selected, color, clear, onMove, selectPiece, askPromote]
  );

  const handleHandClick = useCallback(
    (type) => {
      if (!canAct) return;
      if (dropType === type) {
        clear();
        return;
      }
      setSelected(null);
      setDropType(type);
      setTargets(engine.legalDrops(type, color));
    },
    [canAct, dropType, engine, color, clear]
  );

  const legalTargets = dropType ? targets : targets.map((m) => m.to);

  return { selected, dropType, legalTargets, handleSquareClick, handleHandClick, clear };
}
